import { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, executionsApi } from "@/lib/api-client";
import { toast } from "sonner";
import { Zap, Loader2, ExternalLink, ShieldAlert, Trophy, Activity } from "lucide-react";

const FILTERS = ["active", "completed", "blocked"];
const KIND_META = {
  stripe: { label: "STRIPE", color: "text-violet-300", dot: "bg-violet-400" },
  lob: { label: "LOB MAIL", color: "text-sky-300", dot: "bg-sky-400" },
  vault: { label: "VAULT", color: "text-amber-300", dot: "bg-amber-400" },
  document: { label: "DOC HASH", color: "text-amber-300", dot: "bg-amber-400" },
  revenue: { label: "REVENUE", color: "text-emerald-300", dot: "bg-emerald-400" },
  bridge: { label: "BRIDGE", color: "text-fuchsia-300", dot: "bg-fuchsia-400" },
};

// MANDATE DRAWER — the consolidated Operations surface: Active Mandates in Flight
// + the Victory Ledger, filterable by Active / Completed / Blocked.
export default function MandateDrawer({ open, onOpenChange, initialFilter }) {
  const [filter, setFilter] = useState("active");
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [firing, setFiring] = useState(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (open) setFilter(FILTERS.includes(initialFilter) ? initialFilter : "active");
  }, [open, initialFilter]);

  const load = useCallback(async () => {
    try {
      const [ex, vl] = await Promise.all([
        executionsApi.list(),
        api.get("/victory-ledger").then((r) => r.data).catch(() => null),
      ]);
      setData(ex);
      setLedger(vl);
      setExpired(false);
    } catch (e) {
      // FULFILLMENT TRANSPARENCY — never swallow an auth failure silently
      if (e?.response?.status === 401) setExpired(true);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const fire = async (t, override = false) => {
    setFiring(t.id);
    try {
      const r = override ? await executionsApi.override(t.id) : await executionsApi.run(t.id);
      toast[r.status === "succeeded" ? "success" : "error"](
        `${r.execution_id} — ${r.status === "succeeded" ? "SUCCEEDED" : "HARD-BLOCKED"}` +
        (r.artifact ? ` · ${r.artifact.kind}:${r.artifact.ref}` : ` · trace filed to Vault`));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Execution failed"); }
    finally { setFiring(null); }
  };

  const execs = data?.executions || [];
  const pending = data?.pending_mandates || [];
  const blocked = data?.blocked_mandates || [];
  const inFlight = execs.filter((e) => e.status === "in_flight");
  const succeeded = execs.filter((e) => e.status === "succeeded");
  const failed = execs.filter((e) => e.status === "failed");
  const trophies = ledger?.trophies || [];

  const counts = {
    active: pending.length + inFlight.length,
    completed: succeeded.length + trophies.length,
    blocked: blocked.length + failed.length,
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] bg-[#0A0A0A] border-l border-amber-500/25 p-0 overflow-hidden flex flex-col"
                    data-testid="operations-drawer">
        <div className="px-5 py-4 border-b border-white/10 bg-amber-500/5">
          <SheetTitle className="flex items-center gap-2 font-mono-uc text-[10px] uppercase tracking-[0.3em] text-amber-400/90">
            <Activity size={12} /> Operations · Mandate Drawer
          </SheetTitle>
          <SheetDescription className="text-white/40 text-[11px] mt-1">
            Active Mandates in Flight + the Victory Ledger — every row is a real execution record or provider-confirmed artifact.
          </SheetDescription>
          {/* Status Filter */}
          <div className="flex border border-white/15 mt-3 w-fit" data-testid="mandate-status-filter">
            {FILTERS.map((f) => (
              <button key={f} data-testid={`mandate-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`font-mono-uc text-[10px] uppercase tracking-widest px-3 py-1.5 border-r border-white/15 last:border-r-0 transition-colors ${
                  filter === f
                    ? f === "blocked" ? "bg-rose-500/15 text-rose-300" : f === "completed" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-400"
                    : "text-white/50 hover:text-white/80"}`}>
                {f} · {counts[f]}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto uc-scroll p-4 space-y-2" data-testid="mandate-drawer-body">
          {expired && (
            <div className="border border-rose-500/40 bg-rose-500/[0.06] px-3 py-2.5 flex items-center gap-2 flex-wrap"
                 data-testid="mandate-session-expired">
              <ShieldAlert size={12} className="text-rose-300 flex-none" />
              <span className="font-mono-uc text-[10px] uppercase tracking-widest text-rose-300">
                Session Expired — Re-Login Required
              </span>
              <span className="text-[10px] text-white/50">Mandate data is withheld, not missing.</span>
              <button data-testid="mandate-relogin-btn" onClick={() => (window.location.href = "/login")}
                      className="ml-auto uc-chip text-rose-300 hover:bg-rose-500/10 hover:border-rose-500/50">
                Re-Login
              </button>
            </div>
          )}
          {!data && !expired && <div className="text-white/40 italic text-xs">Loading roster…</div>}

          {/* ACTIVE */}
          {filter === "active" && data && (
            <>
              {counts.active === 0 && <EmptyRow label="No active mandates in flight." testid="mandate-empty-active" />}
              {pending.map((t) => (
                <div key={t.id} className="flex items-center gap-3 border border-rose-500/20 bg-rose-500/[0.03] px-3 py-2 flex-wrap"
                     data-testid={`drawer-pending-${t.id}`}>
                  <span className="uc-dot bg-rose-400"></span>
                  <span className="text-xs text-white/85 truncate max-w-[45%]" title={t.title}>{t.title}</span>
                  <span className="font-mono-uc text-[9px] uppercase tracking-widest text-white/55">{(t.source || "").replace("_", " ")}</span>
                  {t.due_date && <span className="font-mono-uc text-[9px] text-white/50">due {new Date(t.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>}
                  <button data-testid={`drawer-execute-${t.id}`} onClick={() => fire(t)} disabled={firing === t.id}
                          className="ml-auto uc-chip flex items-center gap-1.5 hover:border-amber-500/60 hover:bg-amber-500/10 text-amber-400 disabled:opacity-50">
                    {firing === t.id ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
                    Execute Now
                  </button>
                </div>
              ))}
              {inFlight.map((e) => <ExecutionRow key={e.id} e={e} />)}
            </>
          )}

          {/* COMPLETED */}
          {filter === "completed" && data && (
            <>
              {counts.completed === 0 && <EmptyRow label="No completed mandates yet." testid="mandate-empty-completed" />}
              {ledger?.pnl && (
                <div className="grid grid-cols-3 gap-2 mb-3" data-testid="directorate-pnl">
                  <div className="border border-emerald-500/25 bg-emerald-500/[0.04] px-2 py-2">
                    <div className="font-mono-uc text-[8px] uppercase tracking-widest text-white/45">Cash Revenue Generated</div>
                    <div className="font-serif-display text-lg text-emerald-300" data-testid="pnl-cash-revenue">
                      ${Number(ledger.pnl.cash_revenue_generated || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="border border-sky-500/25 bg-sky-500/[0.04] px-2 py-2">
                    <div className="font-mono-uc text-[8px] uppercase tracking-widest text-white/45">Maintenance Reinvested</div>
                    <div className="font-serif-display text-lg text-sky-300" data-testid="pnl-reinvested">
                      ${Number(ledger.pnl.maintenance_reinvested || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="border border-amber-500/30 bg-amber-500/[0.05] px-2 py-2">
                    <div className="font-mono-uc text-[8px] uppercase tracking-widest text-white/45">Net Directorate Profit</div>
                    <div className="font-serif-display text-lg text-amber-300" data-testid="pnl-net-profit">
                      ${Number(ledger.pnl.net_directorate_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              )}
              {succeeded.slice(0, 20).map((e) => <ExecutionRow key={e.id} e={e} />)}
              {trophies.length > 0 && (
                <div className="pt-2" data-testid="drawer-victory-ledger">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy size={12} className="text-amber-400" />
                    <span className="font-mono-uc text-[10px] uppercase tracking-[0.25em] text-amber-400/90">
                      Victory Ledger · {ledger.total} provider-confirmed artifacts
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {trophies.map((t) => {
                      const m = KIND_META[t.kind] || KIND_META.vault;
                      return (
                        <div key={t.id} className="p-2.5 border border-white/5 hover:border-amber-500/30 transition-colors"
                             data-testid={`drawer-trophy-${t.id}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`uc-dot ${m.dot}`}></span>
                            <span className={`font-mono-uc text-[9px] uppercase tracking-widest ${m.color}`}>{m.label}</span>
                            <span className="font-mono-uc text-[9px] text-white/35 ml-auto">
                              {t.at ? new Date(t.at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
                            </span>
                          </div>
                          <div className="text-xs text-white/80 truncate" title={t.title}>{t.title}</div>
                          <div className="font-mono-uc text-[9px] text-white/55 mt-0.5 truncate" title={t.proof}>{t.proof}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* BLOCKED */}
          {filter === "blocked" && data && (
            <>
              {counts.blocked === 0 && <EmptyRow label="No blocked mandates. Clean board." testid="mandate-empty-blocked" />}
              {blocked.map((t) => (
                <div key={t.id} className="flex items-center gap-3 border border-rose-500/40 bg-rose-500/[0.06] px-3 py-2 flex-wrap"
                     data-testid={`drawer-blocked-${t.id}`}>
                  <ShieldAlert size={12} className="text-rose-400" />
                  <span className="text-xs text-white/90 truncate max-w-[40%]" title={t.title}>{t.title}</span>
                  <span className="font-mono-uc text-[9px] uppercase tracking-widest text-rose-300">HARD-BLOCKED</span>
                  <span className="font-mono-uc text-[9px] text-white/55 truncate max-w-[28%]" title={t.block_reason}>{t.block_reason}</span>
                  <button data-testid={`drawer-override-${t.id}`} onClick={() => fire(t, true)} disabled={firing === t.id}
                          className="ml-auto uc-chip flex items-center gap-1.5 border-rose-500/50 hover:bg-rose-500/10 text-rose-300 disabled:opacity-50">
                    {firing === t.id ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
                    Sovereign Override
                  </button>
                </div>
              ))}
              {failed.map((e) => <ExecutionRow key={e.id} e={e} />)}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function EmptyRow({ label, testid }) {
  return <div className="text-white/40 italic text-xs px-1 py-2" data-testid={testid}>{label}</div>;
}

const provColor = {
  stripe: "text-emerald-300 border-emerald-500/40",
  lob: "text-sky-300 border-sky-500/40",
  vault: "text-amber-300 border-amber-500/40",
};

function ExecutionRow({ e }) {
  return (
    <div className="flex items-center gap-2.5 border border-white/5 px-3 py-1.5 flex-wrap hover:border-white/15 transition-colors"
         data-testid={`drawer-execution-${e.id}`}>
      <span className={`uc-dot ${e.status === "in_flight" ? "bg-amber-400 uc-pulsing" : e.status === "succeeded" ? "bg-emerald-400" : "bg-rose-400"}`}></span>
      <span className={`font-mono-uc text-[8px] uppercase tracking-widest px-1 py-0.5 border ${provColor[e.provider] || "text-white/50 border-white/20"}`}>{e.provider}</span>
      <span className="text-[11px] text-white/70 truncate max-w-[34%]" title={e.task_title}>{e.task_title}</span>
      <span className={`font-mono-uc text-[9px] uppercase tracking-widest ${e.status === "succeeded" ? "text-emerald-300" : e.status === "failed" ? "text-rose-300" : "text-amber-300"}`}>
        {e.status.replace("_", " ")}
      </span>
      <span className="ml-auto font-mono-uc text-[9px] text-white/55 truncate max-w-[26%]" title={e.artifact ? `${e.artifact.kind}:${e.artifact.ref}` : e.detail || ""}>
        {e.artifact ? `${e.artifact.kind}:${e.artifact.ref}` : (e.detail || "").slice(0, 50)}
      </span>
      {e.artifact?.url && (
        <a href={e.artifact.url} target="_blank" rel="noreferrer" className="text-white/40 hover:text-amber-400"><ExternalLink size={11} /></a>
      )}
    </div>
  );
}
